import React from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { fetchEndeavorFeed } from 'ducks/endeavor.reducer';
import ArrayHelper from 'foundation/array-helper';
import { getPlayerSitecoreData } from 'general/js/helpers';
import { ReactModal } from 'components/modal';
import { BlockPlaceholder } from 'components/block-placeholder';
import { BlockSpinner } from 'components/block-spinner';
import { LivePreMessage, bemClasses } from 'components/live-pre-message';
import gtm from 'general/js/gtm';
import { fetchArticlesFeed } from '../../../ducks/articles.reducer';
import ScoringLoader, { endpointNames } from '../../../general/js/loaders/scoring-loader';
import LivePlayerLayout from './live-player-layout';
import {
    AIGWO_EVENT_TAG,
    getEndeavorUrl,
    HIGHLIGHTS_INTERVAL_SEC,
    HIGHLIGHTS_TAG,
    playerSitecoreData,
    THEOPEN_EVENT_TAG
} from './enums';

class LivePlayerModal extends React.Component {
    constructor(props) {
        super(props);

        this.highlightsUpdated = null;
        this.latestVideosUpdated = null;
        this.latestArticlesUpdated = null;
        this.state = {
            resetTab: false
        };
    }

    componentDidMount() {
        if (this.props.isLcq) {
            ScoringLoader.subscribe(endpointNames.PLAYERS_LAST_CHANCE_QUALIFIER);
            ScoringLoader.subscribe(endpointNames.LEADERBOARD_LAST_CHANCE_QUALIFIER);
        } else {
            ScoringLoader.subscribe(endpointNames.PLAYERS);
            ScoringLoader.subscribe(endpointNames.LEADERBOARD);
        }

        ScoringLoader.subscribe(endpointNames.LIVE_SCANNER);
        ScoringLoader.subscribe(endpointNames.COURSE);
        ScoringLoader.subscribe(endpointNames.STATISTICS);
        ScoringLoader.subscribe(endpointNames.SHOT_TRACKER);

        this.fetchHighlights();
        this.fetchLatestVideos();
        this.fetchLatestArticles();
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isOpen && this.props.isOpen) {
            this.setState({ resetTab: true });
        }

        if (prevProps.playerId !== this.props.playerId) {
            this.highlightsUpdated = null;
            this.latestVideosUpdated = null;
            this.latestArticlesUpdated = null;
        }
        this.fetchHighlights();
        this.fetchLatestVideos();
        this.fetchLatestArticles();
    }

    componentWillUnmount() {
        if (this.props.isLcq) {
            ScoringLoader.unsubscribe(endpointNames.PLAYERS_LAST_CHANCE_QUALIFIER);
            ScoringLoader.unsubscribe(endpointNames.LEADERBOARD_LAST_CHANCE_QUALIFIER);
        } else {
            ScoringLoader.unsubscribe(endpointNames.PLAYERS);
            ScoringLoader.unsubscribe(endpointNames.LEADERBOARD);
        }

        ScoringLoader.unsubscribe(endpointNames.LIVE_SCANNER);
        ScoringLoader.unsubscribe(endpointNames.COURSE);
        ScoringLoader.unsubscribe(endpointNames.STATISTICS);
        ScoringLoader.unsubscribe(endpointNames.SHOT_TRACKER);
    }

    hide = () => {
        this.props.onClose();
        const { scoringPlayer } = this.props;
        const sitecoreData = get(scoringPlayer, 'sitecoreData') || playerSitecoreData;
        gtm.push('Player Card', 'Close', `${sitecoreData.firstName} ${sitecoreData.lastName}`);
        this.setState({ resetTab: true });
    }

    onResetTab = () => {
        this.setState({ resetTab: false });
    };

    fetchHighlights() {
        const { scoringPlayer } = this.props;
        const sitecoreData = get(scoringPlayer, 'sitecoreData');
        const currentDate = new Date();
        const shouldFetch = (currentDate - this.highlightsUpdated) / 1000 > HIGHLIGHTS_INTERVAL_SEC;

        if (this.props.liveScanner === undefined) return null;

        if (sitecoreData && shouldFetch) {
            this.highlightsUpdated = currentDate;
            const callbackUrl = this.constructEndeavorHighlightsURL(sitecoreData);
            this.props.fetchEndeavorHighlights(callbackUrl);
        }

        return null;
    }

    fetchLatestVideos() {
        const { scoringPlayer } = this.props;
        const sitecoreData = get(scoringPlayer, 'sitecoreData');
        const currentDate = new Date();
        const shouldFetch = (currentDate - this.latestVideosUpdated) / 1000 > 60;

        if (this.props.liveScanner === undefined) return null;
        if (this.props.liveScanner.hideLatestNews === true) return null;

        const isWomensOpenWebsiteContext = this.props.liveScanner.isWomensOpenWebsiteContext;

        if (sitecoreData && shouldFetch) {
            this.latestVideosUpdated = currentDate;
            const callbackUrl = this.constructEndeavorLatestVideosURL(sitecoreData, isWomensOpenWebsiteContext);
            this.props.fetchEndeavorLatestVideos(callbackUrl);
        }

        return null;
    }

    fetchLatestArticles() {
        const { scoringPlayer } = this.props;
        const sitecoreData = get(scoringPlayer, 'sitecoreData');
        const currentDate = new Date();
        const shouldFetch = (currentDate - this.latestArticlesUpdated) / 1000 > 60;

        if (this.props.liveScanner === undefined) return null;
        if (this.props.liveScanner.hideLatestNews === true) return null;

        if (sitecoreData && shouldFetch) {
            this.latestArticlesUpdated = currentDate;
            const callbackUrl = this.constructLatestArticlesURL(sitecoreData);
            this.props.fetchLatestArticles(callbackUrl);
        }

        return null;
    }

    constructEndeavorHighlightsURL(sitecoreData) {
        const tags = [
            `${sitecoreData.firstName}_${sitecoreData.lastName}_${HIGHLIGHTS_TAG}`,
        ];

        const callbackUrl = getEndeavorUrl(tags);

        return callbackUrl;
    }

    constructEndeavorLatestVideosURL(sitecoreData, isWomensOpenWebsiteContext) {
        const eventYear = window.appState.eventYear;
        const eventTag = isWomensOpenWebsiteContext ?
            AIGWO_EVENT_TAG :
            THEOPEN_EVENT_TAG;
        const tags = [
            `${sitecoreData.firstName}_${sitecoreData.lastName}`,
            `${eventTag}${eventYear}`,
        ];

        const callbackUrl = getEndeavorUrl(tags, 7);

        return callbackUrl;
    }

    constructLatestArticlesURL(sitecoreData) {
        const { liveScanner } = this.props;

        const tags = [
            sitecoreData.sitecoreId
        ];
        const callbackUrl = `/api/NewsFeedApi/GetNewsByFilters?newsfeedParentItemGuid=${liveScanner.newsFolderConfig.newsFolderId}&tags=${tags}`;
        return callbackUrl;
    }


    getEndeavorCloudHighlights() {
        const { scoringPlayer, endeavorReducer } = this.props;
        const sitecoreData = get(scoringPlayer, 'sitecoreData');

        if (this.props.liveScanner === undefined) return null;

        if (!sitecoreData) return null;

        const callbackUrl = this.constructEndeavorHighlightsURL(sitecoreData);
        return endeavorReducer[callbackUrl];
    }

    getEndeavorLatestVideos() {
        const { scoringPlayer, endeavorReducer } = this.props;
        const sitecoreData = get(scoringPlayer, 'sitecoreData');

        if (this.props.liveScanner === undefined) return null;
        if (this.props.liveScanner.hideLatestNews === true) return null;

        if (!sitecoreData) return null;

        const isWOContext = this.props.liveScanner.isWomensOpenWebsiteContext;
        const callbackUrl = this.constructEndeavorLatestVideosURL(sitecoreData, isWOContext);
        return endeavorReducer[callbackUrl];
    }

    getLatestArticles() {
        const { scoringPlayer, articlesReducer } = this.props;
        const sitecoreData = get(scoringPlayer, 'sitecoreData');

        if (this.props.liveScanner === undefined) return null;
        if (this.props.liveScanner.hideLatestNews === true) return null;

        if (!sitecoreData) return null;

        const callbackUrl = this.constructLatestArticlesURL(sitecoreData);
        return articlesReducer[callbackUrl];
    }


    render() {
        const { isOpen, scoringPlayer, ...rest } = this.props;
        const endeavorHighlightGroups = this.getEndeavorCloudHighlights();
        const endeavorLatestVideos = this.getEndeavorLatestVideos();
        const latestArticles = this.getLatestArticles();
        return (
            <ReactModal isOpen={isOpen} onCloseCb={this.hide} closeModal={!isOpen}>
                <div className="live-player">
                    {
                        scoringPlayer === undefined &&
                        <BlockPlaceholder minHeight={400}>
                            <BlockSpinner />
                        </BlockPlaceholder>
                    }
                    {
                        scoringPlayer === null &&
                        <LivePreMessage
                            model={{
                                title: '',
                                body: 'Player data coming soon'
                            }}
                            bemList={[bemClasses.LIGHT]}
                        />
                    }
                    {
                        <LivePlayerLayout
                            {...rest}
                            scoringPlayer={scoringPlayer}
                            highlightGroups={endeavorHighlightGroups}
                            endeavorLatestVideos={endeavorLatestVideos}
                            latestArticles={latestArticles}
                            resetTab={this.state.resetTab}
                            onResetTab={this.onResetTab}
                        />
                    }
                </div>
            </ReactModal>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const { playerId, isLcq } = ownProps;

    const sitecorePlayers = isLcq ?
        state.scoring[endpointNames.PLAYERS_LAST_CHANCE_QUALIFIER] :
        state.scoring[endpointNames.PLAYERS];

    const scoringLeaderboard = isLcq ?
        state.scoring[endpointNames.LEADERBOARD_LAST_CHANCE_QUALIFIER] :
        state.scoring[endpointNames.LEADERBOARD];

    const getScoringPlayer = () => {
        if (sitecorePlayers === undefined ||
            scoringLeaderboard === undefined) {
            return undefined;
        }

        if (!playerId || !sitecorePlayers) {
            return null;
        }

        let player = {
            id: playerId
        };

        if (scoringLeaderboard &&
            !ArrayHelper.isEmpty(scoringLeaderboard.players)
        ) {
            const foundPlayer = scoringLeaderboard.players.find((scoringPlayer) =>
                scoringPlayer.id.toString() === playerId.toString());

            if (foundPlayer) {
                player = foundPlayer;
            }
        }

        const playerSD = getPlayerSitecoreData(sitecorePlayers, player.id);

        if (playerSD) {
            player.sitecoreData = playerSD;
            return player;
        }

        return null;
    };

    return ({
        liveScanner: state.scoring[endpointNames.LIVE_SCANNER],
        liveShotView: state.scoring[endpointNames.SHOT_TRACKER],
        scoringLeaderboard,
        scoringPlayer: getScoringPlayer(),
        scoringStats: state.scoring[endpointNames.STATISTICS],
        scoringCourse: state.scoring[endpointNames.COURSE],
        sitecorePlayers,
        endeavorReducer: state.endeavorReducer,
        articlesReducer: state.articlesReducer,
    });
};

const mapDispatchToProps = (dispatch) => ({
    fetchEndeavorHighlights: (callbackUrl) => dispatch(fetchEndeavorFeed(callbackUrl)),
    fetchEndeavorLatestVideos: (callbackUrl) => dispatch(fetchEndeavorFeed(callbackUrl)),
    fetchLatestArticles: (callbackUrl) => dispatch(fetchArticlesFeed(callbackUrl))
});

const LivePlayerCardContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(LivePlayerModal);

export default LivePlayerCardContainer;
